import { getContacts } from '../store/selectors/contact.selector';
import { SEARCH_TYPES_KEYS } from '../constants';

const FILTER_KEY_TO_API = {
  SPECIALTY: 'specialty',
  SUBSPECIALTY: 'subspecialty',
  CONTENT_TYPE: 'contentType',
  TARGET_AUDIENCE: 'targetAudience',
  LABELS: 'labels',
};

// SearchType tab → API `filters.type` (EntityType[]). The tab narrows the query
// server-side. `none` is absent → no type filter (all entities). Media maps to
// knowledgeItems for now; the document/media thumbnail split needs backend work.
const SEARCH_TYPE_TO_API_TYPES = {
  [SEARCH_TYPES_KEYS.documents]: ['knowledgeItems'],
  [SEARCH_TYPES_KEYS.media]: ['knowledgeItems'],
  [SEARCH_TYPES_KEYS.contacts]: ['contact', 'users'],
  [SEARCH_TYPES_KEYS.compendium]: ['labValues'],
  [SEARCH_TYPES_KEYS.admission]: ['admissions'],
};

export const mapFiltersToApi = (filters = {}) => {
  const apiFilters = {};
  Object.entries(FILTER_KEY_TO_API).forEach(([reduxKey, apiKey]) => {
    const set = filters[reduxKey];
    if (set instanceof Set && set.size > 0) {
      apiFilters[apiKey] = Array.from(set);
    }
  });

  // The active SearchType tab rides inside `filters` as its raw value; map it to
  // the API `type` array here, right before the request. `none` → no type filter.
  const typeFilter = SEARCH_TYPE_TO_API_TYPES[filters.searchType];
  if (typeFilter) apiFilters.type = typeFilter;

  return apiFilters;
};

const facetsToTagsMap = (facets = {}) => ({
  specialty: facets.specialty || {},
  subspecialty: facets.subspecialty || {},
  contentType: facets.contentType || {},
  targetAudience: facets.targetAudience || {},
  labels: facets.labels || {},
});

const LOCAL_LIST_BY_TYPE = {
  knowledgeItems: state => state.knowledge?.items,
  knowledgeCategories: state => state.knowledge?.categories,
  labValues: state => state.lab?.items,
  labCategories: state => state.lab?.categories,
  admissions: state => state.admission?.categories,
  contact: state => getContacts(state),
  users: state => getContacts(state),
};

const hydrate = (hits = [], state) =>
  hits
    .map(({ id, type, score }) => {
      const item = LOCAL_LIST_BY_TYPE[type]?.(state)?.find(x => x.id === id);
      return item ? { item, type, score } : null;
    })
    .filter(Boolean);

// `groupBy: 'workspace'` response → { workspaces: { [wsId]: hits }, contacts, tagsMap }.
// The synthetic `contact` bucket (users + contacts merged, per SEARCH_API.md) is
// lifted to its own field; the `null` bucket (no workspace) is dropped. Each hit
// keeps its API `type` so the renderer dispatches on it directly.
export const assembleResultsFromApiGroups = ({ groups = {}, facets, count = 0, state }) => {
  const workspaces = {};
  Object.entries(groups).forEach(([key, hits]) => {
    if (key === 'contact' || key === 'null') return;
    workspaces[key] = hydrate(hits, state);
  });

  return {
    workspaces,
    contacts: hydrate(groups.contact, state),
    tagsMap: facetsToTagsMap(facets),
    count,
  };
};

export const emptyResultsShape = () => ({
  workspaces: {},
  contacts: [],
  tagsMap: {
    specialty: {},
    subspecialty: {},
    contentType: {},
    targetAudience: {},
    labels: {},
  },
  count: 0,
});
